/**
 * The site header: brand, section links, language switcher, and the CTA that
 * opens the waitlist.
 *
 * The section links only make sense on the front page, so on a legal page they
 * point back at the front page's anchors rather than at nothing.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useI18n, pathInLocale } from '../i18n';
import { LANGUAGE_COOKIE, LANGUAGE_COOKIE_MAX_AGE } from '../../shared/language';
import type { Locale } from '../../shared/proposal';
import { Button } from './Button';
import { Logo } from './Logo';
import styles from './Header.module.css';

export function Header() {
  const { t, localePath } = useI18n();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // A hairline border fades in once the page moves, so the header reads as flat
  // at rest and as a bar once content is passing underneath it.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    // `passive` promises we will not call preventDefault, which lets the
    // browser keep scrolling on the compositor instead of waiting for us.
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close the mobile menu whenever the route changes, or it stays open on top
  // of the page the visitor just navigated to.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const home = localePath('/');
  const anchor = (id: string) => `${home === '/' ? '' : home}/#${id}`.replace('//#', '/#');

  const sections = [
    { href: anchor('services'), label: t.nav.services },
    { href: anchor('products'), label: t.nav.products },
    { href: anchor('method'), label: t.nav.method },
    { href: anchor('studio'), label: t.nav.studio },
  ];

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ''}`}>
      <div className="container">
        <div className={styles.inner}>
          <Link to={home} className={styles.brand}>
            <Logo size={30} />
          </Link>

          <div className={styles.actions}>
            <LanguageSwitcher />

            <nav className={styles.nav} aria-label={t.nav.menu}>
              {sections.map((item) => (
                <a key={item.href} href={item.href} className={styles.navLink}>
                  {item.label}
                </a>
              ))}
            </nav>

            <span className={styles.desktopCta}>
              <Button variant="primary" size="md" href={anchor('proposal')}>
                {t.nav.cta}
              </Button>
            </span>

            <button
              type="button"
              className={styles.menuToggle}
              onClick={() => setMenuOpen((value) => !value)}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              aria-label={menuOpen ? t.nav.closeMenu : t.nav.menu}
            >
              <span className={styles.menuBars} aria-hidden="true" />
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div className={styles.mobilePanel} id="mobile-menu">
            {sections.map((item) => (
              <a key={item.href} href={item.href} className={styles.mobileLink}>
                {item.label}
              </a>
            ))}
            <Button variant="primary" size="lg" block href={anchor('proposal')}>
              {t.nav.cta}
            </Button>
          </div>
        ) : null}
      </div>
    </header>
  );
}

/**
 * Remember a language the visitor actually asked for.
 *
 * This is the only thing on the site that writes to the visitor's device, and
 * it runs only from the click handler below — never from detection. That is
 * deliberate and it is what the Cookie Policy promises: a cookie recording a
 * preference somebody expressed is exempt from consent under the ePrivacy
 * Directive, so there is still no banner. A cookie recording a guess would not
 * be, and would need one.
 *
 * `SameSite=Lax` because it must survive arriving from an external link, which
 * is the case that matters. `Secure` everywhere except a local http dev server,
 * where the browser would simply discard it.
 */
function remember(locale: Locale): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${LANGUAGE_COOKIE}=${locale}; Max-Age=${LANGUAGE_COOKIE_MAX_AGE}; Path=/; SameSite=Lax` +
    secure;
}

/**
 * Language switcher.
 *
 * Real <Link>s to the other language's URL, not a state toggle — so the choice
 * can be bookmarked, shared and crawled. `hrefLang` tells assistive tech and
 * search engines what is on the other end.
 *
 * The click also records the choice, so that the server stops sending this
 * visitor to the language their system settings imply. Without that, someone
 * whose phone is Spanish but who wants to read English would be redirected back
 * on every fresh visit.
 */
function LanguageSwitcher() {
  const { locale, t } = useI18n();
  const location = useLocation();

  return (
    <div className={styles.langGroup} role="group" aria-label={t.nav.languageLabel}>
      {(['en', 'es'] as const).map((code) => (
        <Link
          key={code}
          to={pathInLocale(location.pathname, code)}
          onClick={() => remember(code)}
          hrefLang={code}
          lang={code}
          className={`${styles.langLink} ${locale === code ? styles.langActive : ''}`}
          aria-current={locale === code ? 'true' : undefined}
        >
          {code}
        </Link>
      ))}
    </div>
  );
}
